import { useQuery } from '@tanstack/react-query';
import { getStripeAccount } from '@util/firestore/payments';
import {
  getIncompletePayoutSections,
  isPayoutBlocked,
  isPayoutVerificationPending,
  PayoutBusinessType,
} from '@util/payoutRequirements';
import { useAuth } from 'context/AuthContext';

// The notification badge pulls this hook into the avatar, which renders on
// product pages, search results and chat, so the account has to stay cached
// well past a single mount.
const STALE_TIME = 5 * 60 * 1000;

const useStripeAccount = () => {
  const { userDoc } = useAuth();
  const accountId = userDoc?.account_id;

  const {
    data: account,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['stripeAccount', accountId],
    queryFn: () => getStripeAccount(),
    enabled: !!accountId,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
  });

  const hasShopAddress = !!userDoc?.addresses?.some((a) => a.is_shop_location);
  const businessType: PayoutBusinessType =
    account?.business_type ?? userDoc?.business_type ?? 'individual';
  const verificationPending = isPayoutVerificationPending(account);

  return {
    account,
    isLoading: !!accountId && isLoading,
    refetch,
    hasShopAddress,
    businessType,
    incompletePayoutSections: getIncompletePayoutSections(
      account,
      hasShopAddress,
      businessType
    ),
    verificationPending,
    // Only an account we have actually loaded can be called incomplete. While
    // it is loading or unavailable the cash out stays on its previous path and
    // the backend remains the authority.
    payoutSetupIncomplete: !!account && isPayoutBlocked(account),
  };
};

export default useStripeAccount;
